import { useCallback, useEffect, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { usePrevious } from 'utils/use-previous';

import client from 'api/apollo-client';
import { GetHighlightQuery, HighlightFieldsFragment, Query } from 'api/graphql-types';
import { getServerPageGetHighlight, getServerPageGetHighlightWithTheme } from 'api/graphql-helper';
import { useAppSelector } from 'redux/store';
import { ITab, ITabItem } from './tab';

export type ILatestTabs = Query | GetHighlightQuery | undefined;

export interface IUseHighlightProps {
	module: HighlightFieldsFragment;
	themeId?: string;
}

export const useHighlight = ({
	module: { newsTab, eventsTab, subsidiesTab, permitsTab, mapsAndStatsTab, topicsTab, wooTab },
	themeId
}: IUseHighlightProps): { tabs: ITab[] } => {
	const intl = useIntl();
	const { contentType, contentTypeId } = useAppSelector((state) => state.settings);

	// Use provided themeId, or fallback to page's theme if we're on a theme page
	const effectiveThemeId = useMemo(() => {
		if (themeId) return themeId;
		if (contentType === 'theme' && contentTypeId) return contentTypeId;
		return undefined;
	}, [themeId, contentType, contentTypeId]);

	const previousThemeId = usePrevious(effectiveThemeId);

	const initialTabs = [newsTab, eventsTab, subsidiesTab, permitsTab, mapsAndStatsTab, topicsTab, wooTab]
		.filter((tab) => !!tab)
		.map((tab) => ({
			...tab,
			items: tab?.items?.map((item) => item.data.attributes).filter((item) => !!item) as ITabItem[]
		}));

	const [latestTabs, setLatestTabs] = useState<ILatestTabs>(undefined);
	const [tabs, setTabs] = useState<ITab[]>(initialTabs);
	const [hasFetched, setHasFetched] = useState(false);

	const getLatest = useCallback(async (): Promise<void> => {
		try {
			let results;

			if (effectiveThemeId) {
				results = await getServerPageGetHighlightWithTheme({ variables: { themeId: effectiveThemeId } }, client);
			} else {
				results = await getServerPageGetHighlight({}, client);
			}

			setLatestTabs(results.props.data);
			setHasFetched(true);
		} catch (error: any) {
			// eslint-disable-next-line no-console
			console.error('Cannot get highlight items', error);
			setHasFetched(true); // Prevent retry loops on error
		}
	}, [effectiveThemeId]);

	const getFallbackData = (): ITab[] => {
		const tabsWithFallbackData = tabs.map((tab) => {
			let fallbackTitle;
			let fallbackItems;

			switch (tab?.__typename) {
				case 'ComponentInternalHighlightNews':
					fallbackTitle = intl.formatMessage({ id: 'modules.highlight.news' });
					fallbackItems = latestTabs?.newsarticles;
					break;
				case 'ComponentInternalHighlightEvents':
					fallbackTitle = intl.formatMessage({ id: 'modules.highlight.events' });
					fallbackItems = latestTabs?.events;
					break;
				case 'ComponentInternalHighlightSubsidies':
					fallbackTitle = intl.formatMessage({ id: 'modules.highlight.subsidies' });
					fallbackItems = latestTabs?.subsidies;
					break;
				case 'ComponentInternalHighlightPermits':
					fallbackTitle = intl.formatMessage({ id: 'modules.highlight.permits' });
					fallbackItems = latestTabs?.permits;
					break;
				case 'ComponentInternalHighlightMapsAndStats':
					fallbackTitle = intl.formatMessage({ id: 'modules.highlight.mapsAndFigures' });
					fallbackItems = latestTabs?.mapsFigures;
					break;
				case 'ComponentInternalHighlightTopics':
					fallbackTitle = intl.formatMessage({ id: 'modules.highlight.conversations' });
					break;
				case 'ComponentInternalHighlightWoo':
					fallbackTitle = intl.formatMessage({ id: 'modules.highlight.woo' });
					fallbackItems = latestTabs?.wooRequests;
					break;

				default:
					break;
			}

			return {
				...tab,
				title: tab?.title || fallbackTitle,
				items: tab?.items && tab?.items.length > 0 ? tab?.items : fallbackItems?.map((item) => item.data.attributes)
			};
		});

		return tabsWithFallbackData as ITab[];
	};

	// Reset hasFetched when theme changes
	useEffect(() => {
		if (previousThemeId !== effectiveThemeId && previousThemeId !== undefined) {
			setHasFetched(false);
		}
	}, [effectiveThemeId, previousThemeId]);

	useEffect(() => {
		const hasEmptyData = initialTabs && initialTabs.some((tab) => !tab.items || tab.items.length === 0);

		if (hasEmptyData && !hasFetched) {
			getLatest();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasFetched]);

	useEffect(() => {
		setTabs(getFallbackData());
	}, [latestTabs]);

	return {
		tabs
	};
};

export default useHighlight;
